import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, UncontrolledTooltip } from 'reactstrap';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';

import { showModal } from '../../actions';
import { showConfirmationModal } from '../../actions/App';
import { fetchTaxonomies, fetchMetadataGroups, fetchCustomFields } from '../../actions/Metadata';

import { APIdeleteMetadataGroup, APIresetDefaultMetadataGroup, APIupdateMetadataGroup } from '../../api/Metadata';

import { addFlashMessage } from '../../helpers/FlashMessages';
import { getDefaultMetadataGroupId } from '../../helpers/Files';

const AdminMetadataGroupListHeader = ({
  metadataGroup,
  collapseOpen,
  handleCollapseOpen,
  handleMetadataGroupModal,
  openConfirmationModal,
  loadMetadataGroups,
  loadTaxonomies,
  loadCustomFields,
  scope,
}) => {
  const intl = useIntl();

  const handleSetAsDefault = (id) => {
    const request = id ?
      APIupdateMetadataGroup({ id, default: true }) :
      APIresetDefaultMetadataGroup();

    request.then(() => {
      addFlashMessage({
        type: 'success',
        text: intl.formatMessage({ defaultMessage: 'Set as default' }),
      });
      loadMetadataGroups();
      loadCustomFields();
      loadTaxonomies();
    }).catch(() => {
      addFlashMessage({
        type: 'danger',
        text: intl.formatMessage({ defaultMessage: 'Something went wrong! Please try again.' }),
      });
    });
  };

  const handleDeleteGroup = () => {
    const modalParams = {
      title: intl.formatMessage({
        defaultMessage: 'Are you sure?',
      }),
      text: intl.formatMessage({
        defaultMessage: 'Are you sure you want to delete this <strong>{name}</strong> Metadata Group?',
      }, {
        name: metadataGroup.name,
      }),
      confirmText: intl.formatMessage({
        defaultMessage: 'Delete',
      }),
      processingText: intl.formatMessage({
        defaultMessage: 'Deleting',
      }),
      onConfirm: () => (
        APIdeleteMetadataGroup(metadataGroup.id).then(() => {
          loadMetadataGroups();
          loadTaxonomies();
          loadCustomFields();
        })
      ),
    };
    openConfirmationModal(modalParams);
  };

  const getColSpan = () => {
    if (scope === 'taxonomies') return 5;
    if (scope === 'custom-fields') return 4;
    return 1;
  };

  const showSetAsDefault = () => {
    if (metadataGroup.id) return true;

    if (getDefaultMetadataGroupId() && !metadataGroup.id) return true;

    return false;
  };

  const isGroupDefault = () => {
    if (metadataGroup.default) return true;

    if (!getDefaultMetadataGroupId() && !metadataGroup.id) return true;

    return false;
  };

  return (
    <tr>
      <th
        colSpan={getColSpan()}
        className="master-header-left"
      >
        <div>
          {metadataGroup.id ? <i className="far fa-folder-open" /> : <i className="far fa-circle" />}
          {metadataGroup.name}
          <Badge color="link">
            {intl.formatMessage(
              { defaultMessage: '{count, plural, one {# Tag Category} other {# Tag Categories}}' },
              { count: metadataGroup.taxonomies ? metadataGroup.taxonomies.length : 0 },
            )}
          </Badge>
          <Badge color="link">
            {intl.formatMessage(
              { defaultMessage: '{count, plural, one {# Custom Field} other {# Custom Fields}}' },
              { count: metadataGroup.custom_fields ? metadataGroup.custom_fields.length : 0 },
            )}
          </Badge>
          {isGroupDefault() && (
            <Badge color="secondary">
              {intl.formatMessage({ defaultMessage: 'Default' })}
            </Badge>
          )}
        </div>
      </th>
      <th
        colSpan={2}
        className="master-header-right"
      >
        <div>
          {showSetAsDefault() && (
            <>
              <Button
                color="link"
                size="sm"
                id={`MetadataGroupButtonSetDefault-${metadataGroup.id}`}
                onClick={() => handleSetAsDefault(metadataGroup.id)}
              >
                <i className="far fa-circle-check" />
              </Button>
              <UncontrolledTooltip
                target={`MetadataGroupButtonSetDefault-${metadataGroup.id}`}
              >
                {intl.formatMessage({ defaultMessage: 'Set as default' })}
              </UncontrolledTooltip>
            </>
          )}
          {metadataGroup.id && (
            <>
              <Button
                color="link"
                size="sm"
                id={`MetadataGroupButtonEdit-${metadataGroup.id}`}
                onClick={() => handleMetadataGroupModal(metadataGroup.id)}
              >
                <i className="far fa-pencil" />
              </Button>
              <UncontrolledTooltip
                target={`MetadataGroupButtonEdit-${metadataGroup.id}`}
              >
                {intl.formatMessage({ defaultMessage: 'Edit' })}
              </UncontrolledTooltip>
            </>
          )}
          {metadataGroup.id && (
            <>
              <Button
                color="link"
                size="sm"
                id={`MetadataGroupButtonDelete-${metadataGroup.id}`}
                onClick={handleDeleteGroup}
              >
                <i className="far fa-trash" />
              </Button>
              <UncontrolledTooltip
                target={`MetadataGroupButtonDelete-${metadataGroup.id}`}
              >
                {intl.formatMessage({ defaultMessage: 'Delete' })}
              </UncontrolledTooltip>
            </>
          )}
          <Button
            color="link"
            size="sm"
            onClick={() => handleCollapseOpen(!collapseOpen)}
            className="mr-2"
          >
            {collapseOpen ? (
              <i className="far fa-chevron-up" />
            ) : (
              <i className="far fa-chevron-down" />
            )}
          </Button>
        </div>
      </th>
    </tr>
  );
};

const mapDispatchToProps = dispatch => ({
  handleMetadataGroupModal: (metadataGroupId) => {
    dispatch(showModal('MetadataGroupModal', {
      className: 'modal-metadata-group',
      mode: 'edit',
      metadataGroupId,
    }));
  },
  openConfirmationModal: (params) => {
    dispatch(showConfirmationModal(params));
  },
  loadMetadataGroups: () => {
    dispatch(fetchMetadataGroups());
  },
  loadTaxonomies: () => {
    dispatch(fetchTaxonomies());
  },
  loadCustomFields: () => {
    dispatch(fetchCustomFields());
  },
});

AdminMetadataGroupListHeader.propTypes = {
  metadataGroup: PropTypes.object.isRequired,
  collapseOpen: PropTypes.bool.isRequired,
  handleCollapseOpen: PropTypes.func.isRequired,
  handleMetadataGroupModal: PropTypes.func.isRequired,
  openConfirmationModal: PropTypes.func.isRequired,
  loadTaxonomies: PropTypes.func.isRequired,
  loadCustomFields: PropTypes.func.isRequired,
  loadMetadataGroups: PropTypes.func.isRequired,
  scope: PropTypes.string.isRequired,
};

export default connect(null, mapDispatchToProps)(AdminMetadataGroupListHeader);
